import { atom, useAtom, useSetAtom } from 'jotai';
import { UserPlus } from 'lucide-react';
import dynamic from 'next/dynamic';
import { useCallback } from 'react';

import { IS_SPECIALTY_IN_ORGANISATION_SURVEY_ENABLED } from '@/components/constants/flags';
import { AppDirectionProvider } from '@/providers/direction-provider';
import { Button } from '@heidi/design-system/button';
import { TooltipProvider } from '@heidi/design-system/tooltip';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';

// Lazy so the heavy sign-up modal chunk only loads when a dev actually
// force-opens it — the host below renders nothing until then.
const SignUpModal = dynamic(() => import('@/components/modal/sign-up-modal'), {
  ssr: false,
});

/**
 * Lives at module scope (not section-local state) because the dev-tools
 * sheet unmounts its content on close: the trigger sets this atom and then
 * closes the sheet, and the always-mounted {@link SignUpModalDevHost}
 * renders the modal from it.
 */
const signUpModalDevOpenAtom = atom(false);

interface SignUpModalDevSectionProps {
  /** Called after the user triggers an action that should close the dev-tools sheet. */
  onRequestClose?: () => void;
}

/**
 * Dev-tools shortcut for the initial user onboarding modal (`SignUpModal`,
 * normally only reachable logged-out on `/register`). Mirrors the
 * `register.tsx` props except `trackSignUpFlowStarted`, so dev opens don't
 * emit signup-funnel analytics.
 */
export function SignUpModalDevSection({
  onRequestClose,
}: Readonly<SignUpModalDevSectionProps>) {
  const setIsOpen = useSetAtom(signUpModalDevOpenAtom);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    onRequestClose?.();
  }, [setIsOpen, onRequestClose]);

  return (
    <section aria-labelledby="dev-tools-sign-up-modal-heading" className="mb-6">
      <TypographyP2SemiBold id="dev-tools-sign-up-modal-heading" className="mb-2">
        Sign-up modal
      </TypographyP2SemiBold>
      <TypographyCaption className="mb-3 text-text-tertiary">
        Force-opens the <span className="font-mono">/register</span> onboarding modal
        in-app for visual testing (e.g. the phone number field, which appears once a
        country with a phone variant is selected). Submitting will really attempt a
        sign-up — close the modal instead.
      </TypographyCaption>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full"
        onClick={handleOpen}
        data-testid="dev-tools-sign-up-modal-open"
      >
        <UserPlus size={14} aria-hidden /> Open sign-up modal
      </Button>
    </section>
  );
}

/**
 * Always-mounted counterpart to {@link SignUpModalDevSection}. Rendered once
 * by `DevToolsSidebar` (outside the sheet) so the modal survives the sheet
 * closing. Renders nothing until the atom flips on.
 */
export function SignUpModalDevHost() {
  const [isOpen, setIsOpen] = useAtom(signUpModalDevOpenAtom);

  if (!isOpen) return null;

  return (
    // DevToolsSidebar mounts above TooltipProvider / AppDirectionProvider in
    // `_app.tsx`, so re-establish both here: the modal's child tree uses
    // Tooltip (hard crash without the provider), and Radix direction context
    // keeps RTL behaviour faithful when testing RTL locales.
    <AppDirectionProvider>
      <TooltipProvider delayDuration={350}>
        <SignUpModal
          title="variant-1"
          subtitle="variant-1"
          isOpen
          setOpen={setIsOpen}
          showClinicianTypeSelect={!IS_SPECIALTY_IN_ORGANISATION_SURVEY_ENABLED()}
          showCountrySelect
          shouldBlurOverlay
          showFirstName
          showLastName
          shouldShowLogin
        />
      </TooltipProvider>
    </AppDirectionProvider>
  );
}
